/** Versioned Wall package fixtures for explicit debug URLs. */

import type { CanonicalFixture } from './types';
import { buildCanonicalPackage } from './package';

const WALL_SCHEMA_VERSION = '1.0.0';
const COMPAT: readonly string[] = Object.freeze([WALL_SCHEMA_VERSION]);

const wrapperContext = Object.freeze({
    BuildingType: 'SingleFamily',
    PlaceName: 'Dublin, Ireland',
    Latitude: 53.3498,
    Longitude: -6.2603,
    NumberOfLevels: 2,
});

export const wallFixtures: readonly CanonicalFixture[] = Object.freeze([
    {
        elementType: 'wall',
        variant: 'compliant',
        schemaVersion: WALL_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Wall',
        payload: buildCanonicalPackage('wall', {
            id: 'wall-1',
            type: 'CanonicalWall',
            properties: {
                lengthMm: 5200,
                heightMm: 2700,
                thicknessMm: 300,
                uValueWm2K: 0.18,
                fireRatingMinutes: 60,
                isExternal: true,
                isLoadBearing: true,
                storeyRef: 'storey-1',
                materialLayers: [
                    { name: 'Plaster', thicknessMm: 13 },
                    { name: 'Blockwork Inner Leaf', thicknessMm: 100 },
                    { name: 'Insulation (Mineral Wool)', thicknessMm: 150 },
                    { name: 'Cavity', thicknessMm: 50 },
                    { name: 'Brick Outer Leaf', thicknessMm: 100 },
                ],
            },
        }),
        metadata: {
            description: 'External load-bearing wall that satisfies TGD Part L U-value and fire-rating targets.',
            expectedRuleIds: ['IE-TGD-L-2022-WALL-CONTEXT'],
            expectedVerdict: 'inconclusive',
            expectedCitationUrns: ['legis://IE/TGD-L/2022#table-1'],
            expectedEvidence: 'narrative',
        },
    },
    {
        elementType: 'wall',
        variant: 'noncompliant',
        schemaVersion: WALL_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Wall',
        payload: buildCanonicalPackage('wall', {
            id: 'wall-noncompliant-1',
            type: 'CanonicalWall',
            properties: {
                lengthMm: 5200,
                heightMm: 2700,
                thicknessMm: 300,
                uValueWm2K: 0.55,
                fireRatingMinutes: 15,
                isExternal: true,
                isLoadBearing: true,
                storeyRef: 'storey-1',
                materialLayers: [
                    { name: 'Plaster', thicknessMm: 13 },
                    { name: 'Blockwork Inner Leaf', thicknessMm: 100 },
                    { name: 'Insulation (Mineral Wool)', thicknessMm: 30 },
                    { name: 'Brick Outer Leaf', thicknessMm: 100 },
                ],
            },
        }),
        metadata: {
            description: 'External wall whose U-value (0.55) exceeds Part L 0.18 limit and fire rating below 30 minutes.',
            expectedRuleIds: ['IE-TGD-L-2022-WALL-CONTEXT'],
            expectedVerdict: 'inconclusive',
            expectedCitationUrns: ['legis://IE/TGD-L/2022#table-1'],
            expectedEvidence: 'narrative',
        },
    },
    {
        elementType: 'wall',
        variant: 'missing-input',
        schemaVersion: WALL_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Wall',
        payload: buildCanonicalPackage('wall', {
            id: 'wall-missing-1',
            type: 'CanonicalWall',
            properties: {
                lengthMm: 5200,
                heightMm: 2700,
                thicknessMm: 300,
                // uValueWm2K deliberately omitted
                // fireRatingMinutes deliberately omitted
                isExternal: true,
                isLoadBearing: true,
                storeyRef: 'storey-1',
                materialLayers: [],
            },
        }),
        metadata: {
            description: 'External wall missing uValueWm2K and fireRatingMinutes; the live API accepts it and streams the cited narrative rule as inconclusive.',
            expectedRuleIds: ['IE-TGD-L-2022-WALL-CONTEXT'],
            expectedVerdict: 'inconclusive',
            expectedCitationUrns: ['legis://IE/TGD-L/2022#table-1'],
            expectedEvidence: 'narrative',
            missingInput: true,
        },
    },
    {
        elementType: 'wall',
        variant: 'invalid',
        schemaVersion: WALL_SCHEMA_VERSION,
        compatibleSchemaVersions: COMPAT,
        wrapperContext,
        defaultElementType: 'Wall',
        payload: buildCanonicalPackage('wall', {
            id: 'wall-invalid-1',
            type: 'CanonicalWall',
            properties: {
                lengthMm: -100,
                heightMm: 'two thousand seven hundred' as unknown as number,
                thicknessMm: 300,
                uValueWm2K: -0.5,
                fireRatingMinutes: 'unknown' as unknown as number,
                isExternal: 'yes' as unknown as boolean,
                isLoadBearing: true,
                storeyRef: 'storey-1',
                materialLayers: 'plaster + blockwork' as unknown as Array<Record<string, unknown>>,
            },
        }),
        metadata: {
            description: 'Wall payload with wrong types and negative dimensions; the live API accepts it and streams the cited narrative rule as inconclusive.',
            expectedRuleIds: ['IE-TGD-L-2022-WALL-CONTEXT'],
            expectedVerdict: 'inconclusive',
            expectedCitationUrns: ['legis://IE/TGD-L/2022#table-1'],
            expectedEvidence: 'narrative',
            invalid: true,
        },
    },
]);
